import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import {
  add,
  getISODay,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  max as getMaxDate,
  min as getMinDate,
  set,
} from 'date-fns';
import toast from 'react-hot-toast';

import { getStorageItem, removeStorageItem, setStorageItem } from 'src/utils/storage';
import type { AppThunk } from 'src/store';
import { CampaignCartItemDto, ProductCartItemDto } from '../types/generated';
import { getNextWorkingDayData, getTimeDate } from 'src/utils/salepoint';
import { toSQLDate } from 'src/utils/time';
import { shouldRestoreCartFromStorage } from 'src/utils/order';
import {
  trackBeginCheckout,
  trackItemAddedToCart,
  trackItemRemovedFromCart,
  trackCartItemQuantityChange,
  trackVoucherApplied,
  trackVoucherRemoved,
} from 'src/utils/analytics';
import API from 'src/api';
import { VoucherRecommendation } from 'src/api/pizzaKioskModules/vouchers';
import { i18next } from 'src/hooks/useDataLoading';
import { calculateCartTotal } from 'src/utils/cart';

export type CartItem = Partial<ProductCartItemDto & CampaignCartItemDto> & {
  isVoucherFreeProduct?: boolean;
  voucherCode?: string;
  originalPrice?: number;
  freeProductName?: string;
  freeProductImageUrl?: string;
};

export interface FreeProductInfo {
  id: number;
  name: string;
  price: number;
  imageUrl?: string;
  ektacoCode?: string;
}

export interface AppliedVoucherState {
  id: number;
  code: string;
  type: 'fixed_discount' | 'percentage_product' | 'percentage_cart' | 'free_product' | 'conditional_free' | 'gift_card';
  discountValue?: number;
  giftCardAmount?: number;
  name?: string;
  description?: string;
  discountAmount: number;
  freeProducts?: FreeProductInfo[];
}

export interface VoucherValidationState {
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  needsRevalidation: boolean;
}

export interface CartState {
  deadline: string;
  minDeadline: string;
  maxDeadline: string;
  cartItems: CartItem[];
  showClosedSalePointWarning: boolean;
  showSalePointNotAvailableWarning: boolean;
  showTooLongCheckoutTimeWarning: boolean;
  productsCheckoutTime: number;
  wasPaymentInvokedFromCart: boolean;
  appliedVoucher: AppliedVoucherState | null;
  voucherValidation: VoucherValidationState;
  voucherRecommendations: VoucherRecommendation[];
}

interface InitCartPayload {
  cartItems: CartItem[];
  deadlineData: {
    deadline: string;
    minDeadline: string;
    maxDeadline: string;
  };
  appliedVoucher?: AppliedVoucherState;
}

interface CartQtyPayload {
  cartItemId: string;
  qty: number;
}

interface DeadlineValidationActionPayload {
  newDate?: Date;
  showWarningIfClosed?: boolean;
  extraCheckoutTime?: number; // Can be negative, in case if we want to reduce checkout time during some validation (for example during validation right before order creation)
}

interface DeadlineValidationReducerPayload {
  min?: string;
  max?: string;
  newDeadline?: string;
  showClosedSalePointWarning?: boolean;
  showSalePointNotAvailableWarning?: boolean;
  showTooLongCheckoutTimeWarning?: boolean;
}

interface SetBonusedQtyPayload {
  cartItemId: string;
  bonusedQuantity: number;
}

const deadlineFormat = (date: Date) => {
  return date.toISOString();
};

const initialState: CartState = {
  deadline: null,
  minDeadline: null,
  maxDeadline: null,
  cartItems: [],
  showClosedSalePointWarning: false,
  showSalePointNotAvailableWarning: false,
  showTooLongCheckoutTimeWarning: false,
  productsCheckoutTime: 0,
  // Will regulate if payment page should be displayed, or user should be redirected to cart.
  // Only from /tabs/cart payment page can be opened at the very first time, after from any link.
  // Workaround for ionic-tabs navigation stack cache.
  // After making first order and being redirected from payment page -> thank-you -> main page -
  // cart tab opening should open cart view, not payment.
  wasPaymentInvokedFromCart: false,
  appliedVoucher: null,
  voucherValidation: {
    loading: false,
    error: null,
    errorCode: null,
    needsRevalidation: false,
  },
  voucherRecommendations: [],
};

const slice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    initCart(state: CartState, action: PayloadAction<InitCartPayload>): void {
      const { cartItems, deadlineData, appliedVoucher } = action.payload;
      state.cartItems = cartItems || [];
      state.minDeadline = deadlineData?.minDeadline || null;
      state.maxDeadline = deadlineData?.maxDeadline || null;
      state.deadline = deadlineData?.deadline || null;
      state.appliedVoucher = appliedVoucher || null;
    },
    addCartItem(state: CartState, action: PayloadAction<CartItem>): void {
      const newCartItem = action.payload;
      if (newCartItem.isCampaign) {
        state.cartItems = state.cartItems.concat(newCartItem);
        state.voucherValidation.needsRevalidation = true;
        return;
      }

      const checkPartialsEquality = (partial, otherPartials) => {
        const otherPartial = otherPartials.find((x) => x.partialProductId === partial.partialProductId);
        return partial.quantity === otherPartial?.quantity;
      };
      let areIdenticalCartItems = false;
      state.cartItems = state.cartItems.map((cartItem) => {
        let { quantity } = cartItem;
        if (!areIdenticalCartItems && newCartItem.productId === cartItem.productId) {
          const newPartials = newCartItem?.partials || [];
          const existingPartials = cartItem?.partials || [];
          const newVariantIds = newCartItem?.variantIds || [];
          const existingVariantIds = cartItem?.variantIds || [];
          const areNewPartialsEqual = newPartials.every((p) => checkPartialsEquality(p, existingPartials));
          const areExistingPartialsEqual = existingPartials.every((p) => checkPartialsEquality(p, newPartials));
          const areVariantsEqual = newVariantIds.every((newVariantId) => existingVariantIds.includes(newVariantId));
          if (areNewPartialsEqual && areExistingPartialsEqual && areVariantsEqual) {
            quantity += newCartItem.quantity;
            areIdenticalCartItems = true;
          }
        }
        return { ...cartItem, quantity };
      });
      if (!areIdenticalCartItems) {
        state.cartItems = state.cartItems.concat(newCartItem);
      }
      state.voucherValidation.needsRevalidation = true;
    },
    removeCartItem(state: CartState, action: PayloadAction<string>): void {
      state.cartItems = state.cartItems.filter((cartItem) => cartItem.uniqId !== action.payload);
      state.voucherValidation.needsRevalidation = true;
      if (state.cartItems.length <= 0) {
        state.minDeadline = deadlineFormat(new Date());
        state.maxDeadline = deadlineFormat(new Date());
        state.deadline = deadlineFormat(new Date());
        state.showClosedSalePointWarning = false;
      }
    },
    setDeadline(state: CartState, action: PayloadAction<string>): void {
      state.deadline = action.payload;
    },
    setProductsCheckoutTime(state: CartState, action: PayloadAction<number>): void {
      state.productsCheckoutTime = action.payload;
    },
    setCartItemQty(state: CartState, action: PayloadAction<CartQtyPayload>): void {
      const { cartItemId, qty } = action.payload;
      state.cartItems = state.cartItems.map((cartItem) => {
        return cartItem.uniqId === cartItemId ? { ...cartItem, quantity: qty } : cartItem;
      });
      state.voucherValidation.needsRevalidation = true;
    },
    setDeadlines(state: CartState, action: PayloadAction<DeadlineValidationReducerPayload>): void {
      const {
        min = null,
        max = null,
        newDeadline = null,
        showClosedSalePointWarning = false,
        showSalePointNotAvailableWarning = false,
        showTooLongCheckoutTimeWarning = false,
      } = action.payload;
      state.minDeadline = min;
      state.maxDeadline = max;
      state.deadline = newDeadline || min;
      state.showClosedSalePointWarning = Boolean(showClosedSalePointWarning);
      state.showSalePointNotAvailableWarning = Boolean(showSalePointNotAvailableWarning);
      state.showTooLongCheckoutTimeWarning = Boolean(showTooLongCheckoutTimeWarning);
    },
    setShowClosedSalePointWarning(state: CartState, action: PayloadAction<boolean>): void {
      state.showClosedSalePointWarning = action.payload;
    },
    setShowSalePointNotAvailableWarning(state: CartState, action: PayloadAction<boolean>): void {
      state.showSalePointNotAvailableWarning = action.payload;
    },
    setShowTooLongCheckoutTimeWarning(state: CartState, action: PayloadAction<boolean>): void {
      state.showTooLongCheckoutTimeWarning = action.payload;
    },
    resetCart(state: CartState): void {
      state.cartItems = [];
      state.minDeadline = null;
      state.maxDeadline = null;
      state.deadline = null;
      state.showClosedSalePointWarning = false;
      state.showSalePointNotAvailableWarning = false;
      state.appliedVoucher = null;
      state.voucherValidation = {
        loading: false,
        error: null,
        errorCode: null,
        needsRevalidation: false,
      };
      state.voucherRecommendations = [];
    },
    setBonusedQty(state: CartState, action: PayloadAction<SetBonusedQtyPayload>): void {
      const { bonusedQuantity, cartItemId } = action.payload;
      state.cartItems = state.cartItems.map((cartItem) => {
        if (cartItem.uniqId === cartItemId) {
          return { ...cartItem, bonusedQuantity };
        }

        return cartItem;
      });
      // Points-paid lines get no voucher benefit: discount and "buy X get Y" depend on them
      state.voucherValidation.needsRevalidation = true;
    },
    setPaymentInvoked(state: CartState, action: PayloadAction<boolean>): void {
      state.wasPaymentInvokedFromCart = action.payload;
    },
    removeAllBonusedQty(state: CartState): void {
      state.cartItems = state.cartItems.map((cartItem) => {
        return { ...cartItem, bonusedQuantity: 0 };
      });
      state.voucherValidation.needsRevalidation = true;
    },
    updateCart(state: CartState, action: PayloadAction<CartItem[]>): void {
      state.cartItems = action.payload;
      state.voucherValidation.needsRevalidation = true;
    },
    setVoucherValidationLoading(state: CartState, action: PayloadAction<boolean>): void {
      state.voucherValidation.loading = action.payload;
      if (action.payload) {
        state.voucherValidation.error = null;
        state.voucherValidation.errorCode = null;
      }
    },
    setVoucherValidationError(state: CartState, action: PayloadAction<{ error: string; errorCode?: string }>): void {
      state.voucherValidation.error = action.payload.error;
      state.voucherValidation.errorCode = action.payload.errorCode || null;
      state.voucherValidation.loading = false;
    },
    clearVoucherValidationError(state: CartState): void {
      state.voucherValidation.error = null;
      state.voucherValidation.errorCode = null;
    },
    setAppliedVoucher(state: CartState, action: PayloadAction<AppliedVoucherState>): void {
      state.appliedVoucher = action.payload;
      state.voucherValidation.loading = false;
      state.voucherValidation.error = null;
      state.voucherValidation.errorCode = null;
      state.voucherValidation.needsRevalidation = false;
      state.voucherRecommendations = [];
    },
    removeAppliedVoucher(state: CartState): void {
      state.appliedVoucher = null;
      state.voucherValidation.error = null;
      state.voucherValidation.errorCode = null;
    },
    addFreeProductToCart(state: CartState, action: PayloadAction<CartItem>): void {
      state.cartItems = state.cartItems.concat(action.payload);
    },
    removeFreeProductsFromCart(state: CartState, action: PayloadAction<string>): void {
      // Remove cart items that are voucher free products with matching voucher code
      state.cartItems = state.cartItems.filter(
        (item) => !(item.isVoucherFreeProduct && item.voucherCode === action.payload)
      );
    },
    setVoucherRecommendations(state: CartState, action: PayloadAction<VoucherRecommendation[]>): void {
      state.voucherRecommendations = action.payload;
    },
  },
});

export const { reducer } = slice;

export const clearCartStorage =
  (): AppThunk =>
  async (_dispatch, getState): Promise<void> => {
    const { isAuthenticated } = getState().auth;
    await removeStorageItem('cartItems');
    await removeStorageItem('deadlineData');
    await removeStorageItem('cartItemsExpired');
    await removeStorageItem('appliedVoucher');
    if (isAuthenticated) {
      await removeStorageItem('selectedSalePointId');
    }
  };

export const storeDeadlines =
  (): AppThunk =>
  async (_dispatch, getState): Promise<void> => {
    const cartState = getState().cart;
    await setStorageItem(
      'deadlineData',
      JSON.stringify({
        minDeadline: cartState.minDeadline,
        maxDeadline: cartState.maxDeadline,
        deadline: cartState.deadline,
      })
    );
    await setStorageItem('cartItemsExpired', add(new Date(), { hours: 12 }).toISOString());
  };

export const initCart =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    const storedCartItems = await getStorageItem('cartItems');
    const deadlineData = await getStorageItem('deadlineData');
    const storedVoucher = await getStorageItem('appliedVoucher');
    const restoreCartFromStorage = await shouldRestoreCartFromStorage();
    if (restoreCartFromStorage) {
      let parsedCartItems: CartItem[] = [];
      let verifiedCartItems = parsedCartItems;
      let unverifiedCartItems = [];

      try {
        parsedCartItems = JSON.parse(storedCartItems);
      } catch (error) {
        console.error('Failed to parse cart items from local storage, error: ', error);
        return;
      }

      try {
        const cartVerificationResponse = await API.cart.verifyCartItems(parsedCartItems);
        verifiedCartItems = cartVerificationResponse.verifiedCartItems;
        unverifiedCartItems = cartVerificationResponse.unverifiedCartItems;
      } catch (error) {
        console.error('Error during cart items verification request: ', error);
        dispatch(clearCartStorage());
        return;
      }

      if (unverifiedCartItems.length) {
        toast.error(
          unverifiedCartItems.length === 1
            ? i18next.t('1 cart item was removed')
            : i18next.t('{{cartItemsCount}} cart items were removed', { cartItemsCount: unverifiedCartItems.length })
        );
      }

      await setStorageItem('cartItems', JSON.stringify(verifiedCartItems));
      await setStorageItem('cartItemsExpired', add(new Date(), { hours: 12 }).toISOString());

      if (verifiedCartItems.length) {
        let appliedVoucher: AppliedVoucherState | null = null;

        // Restore voucher if it exists
        if (storedVoucher) {
          try {
            appliedVoucher = JSON.parse(storedVoucher);
          } catch (error) {
            console.error('Failed to parse applied voucher from storage:', error);
            // Remove invalid voucher data
            await removeStorageItem('appliedVoucher');
          }
        }

        dispatch(
          slice.actions.initCart({
            cartItems: verifiedCartItems,
            deadlineData: JSON.parse(deadlineData),
            appliedVoucher,
          })
        );
      } else {
        dispatch(clearCartStorage());
      }
    } else {
      dispatch(clearCartStorage());
    }
  };

const createFreeCartItem = (freeProduct: FreeProductInfo, voucherCode: string): CartItem => ({
  uniqId: `free_${voucherCode}_${freeProduct.id}_${Date.now()}`,
  productId: freeProduct.id,
  quantity: 1,
  isProduct: true,
  partials: [],
  variantIds: [],
  isVoucherFreeProduct: true,
  voucherCode,
  originalPrice: freeProduct.price,
  freeProductName: freeProduct.name,
  freeProductImageUrl: freeProduct.imageUrl,
});

let recommendationTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Check eligible voucher recommendations for current cart (debounced)
 */
export const checkVoucherRecommendations =
  (): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    if (recommendationTimer) clearTimeout(recommendationTimer);

    recommendationTimer = setTimeout(async () => {
      const state = getState();
      const { cartItems, appliedVoucher } = state.cart;
      const { selectedSalePointId } = state.salePoint;

      // Skip if voucher already applied or cart empty
      if (appliedVoucher || !cartItems.length || !selectedSalePointId) {
        dispatch(slice.actions.setVoucherRecommendations([]));
        return;
      }

      try {
        const result = await API.voucher.checkRecommendations(selectedSalePointId, cartItems);
        // Re-check state hasn't changed during API call
        const currentState = getState();
        if (!currentState.cart.appliedVoucher) {
          dispatch(slice.actions.setVoucherRecommendations(result.recommendations));
        }
      } catch (error) {
        console.error('Failed to check voucher recommendations:', error);
      }
    }, 500);
  };

let voucherRevalidationSeq = 0;

/**
 * Revalidate applied voucher after cart change (qty/add/remove).
 * Updates free products and removes invalid vouchers.
 */
const revalidateVoucherAfterCartChange = async (
  dispatch: Parameters<AppThunk>[0],
  getState: Parameters<AppThunk>[1]
): Promise<void> => {
  const { appliedVoucher, cartItems, voucherValidation } = getState().cart;
  if (!appliedVoucher || !voucherValidation.needsRevalidation) return;

  const { selectedSalePointId } = getState().salePoint;
  if (!selectedSalePointId) return;

  const seq = ++voucherRevalidationSeq;
  try {
    const calculatedTotal = calculateCartTotal(cartItems, getState().product.products);
    const result = await API.voucher.validateVoucher(
      appliedVoucher.code,
      selectedSalePointId,
      cartItems,
      calculatedTotal
    );
    // Latest cart wins: drop a response if the cart changed again or the voucher was removed/replaced meanwhile
    if (seq !== voucherRevalidationSeq || getState().cart.appliedVoucher?.code !== appliedVoucher.code) return;
    if (result.isValid && result.voucher) {
      const updatedVoucher: AppliedVoucherState = {
        id: result.voucher.id,
        code: result.voucher.code,
        type: result.voucher.type,
        discountValue: result.voucher.discountValue,
        giftCardAmount: result.voucher.giftCardAmount,
        name: result.voucher.name,
        description: result.voucher.description,
        discountAmount: result.discountAmount,
        freeProducts: result.freeProducts,
      };
      dispatch(slice.actions.setAppliedVoucher(updatedVoucher));
      dispatch(slice.actions.removeFreeProductsFromCart(appliedVoucher.code));
      if (result.freeProducts && result.freeProducts.length > 0) {
        result.freeProducts.forEach((freeProduct) => {
          dispatch(slice.actions.addFreeProductToCart(createFreeCartItem(freeProduct, result.voucher!.code)));
        });
      }
      const updatedState = getState();
      await setStorageItem('cartItems', JSON.stringify(updatedState.cart.cartItems));
      await setStorageItem('appliedVoucher', JSON.stringify(updatedVoucher));
    } else {
      dispatch(slice.actions.removeFreeProductsFromCart(appliedVoucher.code));
      dispatch(slice.actions.removeAppliedVoucher());
      await removeStorageItem('appliedVoucher');
      const updatedState = getState();
      await setStorageItem('cartItems', JSON.stringify(updatedState.cart.cartItems));
      toast.error(result.error || i18next.t('Voucher is no longer valid'));
    }
  } catch (error) {
    console.error('Voucher revalidation failed:', error);
  }
};

export const addCartItem =
  (cartItem: CartItem): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    await dispatch(slice.actions.addCartItem(cartItem));
    const { cart, campaign, product } = getState();
    trackItemAddedToCart(cartItem, product.products, campaign.campaigns);
    await setStorageItem('cartItems', JSON.stringify(cart.cartItems));
    await setStorageItem('cartItemsExpired', add(new Date(), { hours: 12 }).toISOString());
    await revalidateVoucherAfterCartChange(dispatch, getState);
    dispatch(checkVoucherRecommendations());
  };

export const removeCartItem =
  (cartItemId: string): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const { cart, product, campaign } = getState();
    const removedItem = cart.cartItems.find((item) => item.uniqId === cartItemId);
    trackItemRemovedFromCart(removedItem, product.products, campaign.campaigns);
    await dispatch(slice.actions.removeCartItem(cartItemId));
    const { cartItems } = getState().cart;
    if (cartItems.length > 0) {
      await setStorageItem('cartItems', JSON.stringify(cartItems));
      await setStorageItem('cartItemsExpired', add(new Date(), { hours: 12 }).toISOString());
      await revalidateVoucherAfterCartChange(dispatch, getState);
      dispatch(checkVoucherRecommendations());
    } else {
      dispatch(clearCartStorage());
    }
  };

export const setDeadline =
  (deadline: string): AppThunk =>
  async (dispatch): Promise<void> => {
    await dispatch(slice.actions.setDeadline(deadline));
    await dispatch(storeDeadlines());
  };

export const setCartItemQty =
  (payload: CartQtyPayload): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const { cart, product, campaign } = getState();
    const existingCartItem = cart.cartItems.find((item) => item.uniqId === payload.cartItemId);
    const oldQuantity = existingCartItem?.quantity ?? 0;

    await dispatch(slice.actions.setCartItemQty(payload));

    if (existingCartItem) {
      trackCartItemQuantityChange(existingCartItem, oldQuantity, payload.qty, product.products, campaign.campaigns);
    }

    const { cartItems } = getState().cart;
    await setStorageItem('cartItems', JSON.stringify(cartItems));
    await setStorageItem('cartItemsExpired', add(new Date(), { hours: 12 }).toISOString());
    await revalidateVoucherAfterCartChange(dispatch, getState);
    dispatch(checkVoucherRecommendations());
  };

export const setShowClosedSalePointWarning =
  (payload: boolean): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setShowClosedSalePointWarning(payload));
  };

export const setShowSalePointNotAvailableWarning =
  (payload: boolean): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setShowSalePointNotAvailableWarning(payload));
  };

export const setShowTooLongCheckoutTimeWarning =
  (payload: boolean): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setShowTooLongCheckoutTimeWarning(payload));
  };

export const resetCart =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    await dispatch(clearCartStorage());
    await dispatch(slice.actions.resetCart());
  };

export const updateCart =
  (payload: CartItem[]): AppThunk =>
  async (dispatch): Promise<void> => {
    await setStorageItem('cartItems', JSON.stringify(payload));
    await setStorageItem('cartItemsExpired', add(new Date(), { hours: 12 }).toISOString());
    await dispatch(slice.actions.updateCart(payload));
    dispatch(checkVoucherRecommendations());
  };

export const setBonusedQty =
  (payload: SetBonusedQtyPayload): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    dispatch(slice.actions.setBonusedQty(payload));
    const { cartItems } = getState().cart;
    await setStorageItem('cartItems', JSON.stringify(cartItems));
    await setStorageItem('cartItemsExpired', add(new Date(), { hours: 12 }).toISOString());
    await revalidateVoucherAfterCartChange(dispatch, getState);
    dispatch(checkVoucherRecommendations());
  };

export const setProductsCheckoutTime =
  (payload: number): AppThunk =>
  async (dispatch): Promise<void> => {
    dispatch(slice.actions.setProductsCheckoutTime(payload));
  };

export const setPaymentInvoked =
  (payload: boolean, total: number = 0): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const { cart, product, campaign } = getState();
    if (payload) trackBeginCheckout(cart, product.products, campaign.campaigns, total, cart.appliedVoucher);
    dispatch(slice.actions.setPaymentInvoked(payload));
  };

export const getDeadline =
  (): AppThunk =>
  async (dispatch, getState): Promise<string> => {
    const { deadline } = getState().cart;
    return deadline;
  };

export const adjustBonusedQuantityProducts =
  (): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const { cartItems } = getState().cart;
    const { variants } = getState().product;
    const { bonusPoints, bookedBonusPoints } = getState().auth.user;

    const bonusPointsFinal = bonusPoints - bookedBonusPoints;

    const costBonusPoints = cartItems
      .filter((cartItem) => cartItem.isProduct && cartItem.bonusedQuantity)
      .map((cartItem) => {
        const { variantIds, bonusedQuantity } = cartItem;
        const variantData = variants.find((variant) => variantIds.includes(variant.id) && variant.bonusPointsCost);
        return (variantData?.bonusPointsCost || 0) * bonusedQuantity;
      })
      .reduce((acc, bonusPointsCost) => acc + bonusPointsCost, 0);

    if (costBonusPoints > bonusPointsFinal) {
      dispatch(slice.actions.removeAllBonusedQty());
      await setStorageItem('cartItems', JSON.stringify(getState().cart.cartItems));
      await revalidateVoucherAfterCartChange(dispatch, getState);
    }
  };

export const validateDeadline =
  (payload?: DeadlineValidationActionPayload): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const { newDate, showWarningIfClosed, extraCheckoutTime } = payload || {};
    const { salePointWorkingDays, salePointAvailableTimes, salePoints, selectedSalePointId } = getState().salePoint;
    const { deadline, productsCheckoutTime } = getState().cart;
    const salePoint = (salePoints || []).find((s) => s.id === selectedSalePointId);

    if (!salePointWorkingDays?.length || !salePoints?.length || !selectedSalePointId) {
      await dispatch(slice.actions.setDeadlines({}));
      dispatch(clearCartStorage());
      return;
    }

    if (!salePoint?.enabled) {
      await dispatch(slice.actions.setDeadlines({ showSalePointNotAvailableWarning: true }));
      return;
    }

    const { minCheckoutTime, isResetCheckoutTime, defaultMinCheckoutTime } = salePoints.find(
      (s) => s.id === selectedSalePointId
    );
    const salePointCheckoutTime = Math.abs(
      (isResetCheckoutTime && defaultMinCheckoutTime && (newDate || deadline) && !isToday(newDate || new Date(deadline))
        ? defaultMinCheckoutTime
        : minCheckoutTime) + (extraCheckoutTime || 0)
    );
    const checkoutTime = salePointCheckoutTime + (productsCheckoutTime || 0); // Total time (in minutes) which will take for sale point to complete an order.
    let currentDate = new Date();
    currentDate = set(currentDate, {
      minutes: Math.ceil(currentDate.getMinutes() / 5) * 5,
      seconds: 0,
      milliseconds: 0,
    });
    const currentDateWithMinCheckoutTime = add(currentDate, { minutes: salePointCheckoutTime });
    const currentDateWithCheckoutTime = add(currentDate, { minutes: checkoutTime });
    const pickupDate =
      newDate ||
      (deadline && isAfter(new Date(deadline), currentDateWithCheckoutTime)
        ? new Date(deadline)
        : currentDateWithCheckoutTime);
    const availableTimesData = (salePointAvailableTimes || []).find((at) => at.date === toSQLDate(pickupDate));
    const workingDaysData = salePointWorkingDays.find((wd) => wd.isoWeekDate === getISODay(pickupDate));
    const { start, end } = availableTimesData || workingDaysData || { start: null, end: null };
    const isNonWorkingDay = !start || !end;
    const isLateForOrderingToday =
      end && isToday(pickupDate) && isAfter(currentDateWithMinCheckoutTime, getTimeDate(pickupDate, end));

    if (isNonWorkingDay || isLateForOrderingToday) {
      const { nextStartWorkingDate, nextEndWorkingDate } = getNextWorkingDayData(
        salePointWorkingDays,
        salePointAvailableTimes,
        pickupDate
      );

      if (!nextStartWorkingDate || !nextEndWorkingDate) {
        await dispatch(slice.actions.setDeadlines({ showSalePointNotAvailableWarning: true }));
        await dispatch(clearCartStorage());

        return;
      }
      const nextStartWorkingDateWithCheckoutTime = add(nextStartWorkingDate, { minutes: checkoutTime });
      const isNextMinDateAfterNextEndDate = isAfter(nextStartWorkingDateWithCheckoutTime, nextEndWorkingDate);
      await dispatch(
        slice.actions.setDeadlines({
          min: deadlineFormat(add(nextStartWorkingDate, { minutes: checkoutTime })),
          max: deadlineFormat(nextEndWorkingDate),
          newDeadline: deadlineFormat(getMinDate([nextStartWorkingDateWithCheckoutTime, nextEndWorkingDate])),
          showClosedSalePointWarning: showWarningIfClosed && !isNextMinDateAfterNextEndDate,
          showTooLongCheckoutTimeWarning: isNextMinDateAfterNextEndDate,
        })
      );
      await dispatch(storeDeadlines());

      return;
    }

    const salePointStartDate = getTimeDate(pickupDate, start); // Date time when sale point opens up
    const salePointEndDate = getTimeDate(pickupDate, end); // Date time when sale point closes down
    const salePointStartDateWithCheckoutTime = add(salePointStartDate, { minutes: checkoutTime });
    const startDateWithCheckoutTime = getMaxDate([currentDateWithCheckoutTime, salePointStartDateWithCheckoutTime]); // First available date time for receiving current order.

    const todayAvailableTimesData = (salePointAvailableTimes || []).find((at) => at.date === toSQLDate(currentDate));
    const todayWorkingDaysData = salePointWorkingDays.find((wd) => wd.isoWeekDate === getISODay(currentDate));
    const { start: todayStart } = todayAvailableTimesData || todayWorkingDaysData || { start: null, end: null };
    const salePointTodayStartDate = todayStart ? getTimeDate(currentDate, todayStart) : salePointStartDate; // Today date time when sale point opens up

    const isMinDateAfterEndDate = isAfter(startDateWithCheckoutTime, salePointEndDate);
    const isLateEvening = isAfter(pickupDate, salePointEndDate);
    const isEarlyMorning = isBefore(pickupDate, salePointStartDateWithCheckoutTime);

    if (isMinDateAfterEndDate) {
      await dispatch(
        slice.actions.setDeadlines({
          min: deadlineFormat(startDateWithCheckoutTime),
          max: deadlineFormat(salePointEndDate),
          newDeadline: deadlineFormat(salePointEndDate),
          showTooLongCheckoutTimeWarning: true,
        })
      );
      await dispatch(storeDeadlines());

      return;
    }

    if (isEarlyMorning || isLateEvening) {
      await dispatch(
        slice.actions.setDeadlines({
          min: deadlineFormat(startDateWithCheckoutTime),
          max: deadlineFormat(salePointEndDate),
          showClosedSalePointWarning: showWarningIfClosed && isBefore(currentDate, salePointTodayStartDate),
        })
      );
      await dispatch(storeDeadlines());

      return;
    }

    const anotherDayWasPicked = !isSameDay(pickupDate, new Date(deadline));
    await dispatch(
      slice.actions.setDeadlines({
        min: deadlineFormat(startDateWithCheckoutTime),
        max: deadlineFormat(salePointEndDate),
        newDeadline: anotherDayWasPicked
          ? deadlineFormat(startDateWithCheckoutTime)
          : deadlineFormat(getMaxDate([pickupDate, startDateWithCheckoutTime])),
        showClosedSalePointWarning: showWarningIfClosed && isBefore(currentDate, salePointTodayStartDate),
      })
    );
    await dispatch(storeDeadlines());
  };

export const applyVoucher =
  (code: string): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    try {
      dispatch(slice.actions.setVoucherValidationLoading(true));

      const state = getState();
      const { cartItems } = state.cart;
      const { selectedSalePointId } = state.salePoint;

      if (!selectedSalePointId) {
        dispatch(
          slice.actions.setVoucherValidationError({
            error: i18next.t('Please select a sale point first'),
            errorCode: 'NO_SALE_POINT',
          })
        );
        return;
      }

      if (!cartItems || cartItems.length === 0) {
        dispatch(
          slice.actions.setVoucherValidationError({
            error: i18next.t('Your cart is empty'),
            errorCode: 'EMPTY_CART',
          })
        );
        return;
      }

      // Calculate cart total using shared utility
      const calculatedTotal = calculateCartTotal(cartItems, state.product.products);

      const result = await API.voucher.validateVoucher(code, selectedSalePointId, cartItems, calculatedTotal);

      if (result.isValid && result.voucher) {
        const appliedVoucher: AppliedVoucherState = {
          id: result.voucher.id,
          code: result.voucher.code,
          type: result.voucher.type,
          discountValue: result.voucher.discountValue,
          giftCardAmount: result.voucher.giftCardAmount,
          name: result.voucher.name,
          description: result.voucher.description,
          discountAmount: result.discountAmount,
          freeProducts: result.freeProducts,
        };

        // First remove any existing free products from previous voucher
        const currentVoucher = state.cart.appliedVoucher;
        if (currentVoucher) {
          dispatch(slice.actions.removeFreeProductsFromCart(currentVoucher.code));
        }

        dispatch(slice.actions.setAppliedVoucher(appliedVoucher));

        // Add free products as cart items with price 0
        if (result.freeProducts && result.freeProducts.length > 0) {
          result.freeProducts.forEach((freeProduct) => {
            dispatch(slice.actions.addFreeProductToCart(createFreeCartItem(freeProduct, result.voucher.code)));
          });

          // Update cart storage with new items
          const updatedState = getState();
          await setStorageItem('cartItems', JSON.stringify(updatedState.cart.cartItems));
        }

        // Store in device storage for persistence
        await setStorageItem('appliedVoucher', JSON.stringify(appliedVoucher));

        toast.success(i18next.t('Voucher applied successfully'));
        trackVoucherApplied(appliedVoucher);
      } else {
        dispatch(
          slice.actions.setVoucherValidationError({
            error: result.error || i18next.t('Invalid voucher code'),
            errorCode: result.errorCode || 'INVALID_CODE',
          })
        );
      }
    } catch (error) {
      console.error('Voucher application failed:', error);
      dispatch(
        slice.actions.setVoucherValidationError({
          error: i18next.t('Failed to apply voucher'),
          errorCode: 'NETWORK_ERROR',
        })
      );
    }
  };

export const removeVoucher =
  (): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    const state = getState();
    const { appliedVoucher } = state.cart;

    // Remove free products associated with this voucher from cart
    if (appliedVoucher) {
      trackVoucherRemoved(appliedVoucher.code);
      dispatch(slice.actions.removeFreeProductsFromCart(appliedVoucher.code));

      // Update cart storage
      const updatedState = getState();
      if (updatedState.cart.cartItems.length > 0) {
        await setStorageItem('cartItems', JSON.stringify(updatedState.cart.cartItems));
      }
    }

    dispatch(slice.actions.removeAppliedVoucher());
    await removeStorageItem('appliedVoucher');
    toast.success(i18next.t('Voucher removed'));
    dispatch(checkVoucherRecommendations());
  };

export const clearVoucherError =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.clearVoucherValidationError());
  };

export default slice;
